import { useQuery } from '@tanstack/react-query';
import { RefObject } from 'preact';
import { useEffect, useRef } from 'preact/hooks';

import HabitatLiveStreamPlayer from '@/components/HabitatLiveStreamPlayer';
import { useHabitatLiveStreamPlayerContext } from '@/components/HabitatLiveStreamPlayer/contexts';
import { useIsMobileLayout } from '@/components/LayoutDetector/hooks';
import LiveStreamPlayer from '@/components/LiveStreamPlayer';
import { Advertisement } from '@/queries/advertisements/types';
import { getHabitatQueryOptions } from '@/queries/habitats';
import { Camera, HabitatListItem } from '@/queries/habitats/types';
import { useSelector } from '@/redux/helper';
import { selectIsFreeUser } from '@/redux/selectors/users';

import HomeVideoPlayer from '../HomeVideoPlayer';
import HomeAdvertisementOverlay from './HomeAdvertisementOverlay';
import HomeLiveStreamPlayerFakeMobileControls from './HomeLiveStreamPlayerFakeMobileControls';
import HomeLiveStreamPlayerOverlay from './HomeLiveStreamPlayerOverlay';

interface HomeLiveStreamPlayerProps {
  habitat: HabitatListItem;
  miniMode?: boolean;
  preferLiveStreamMutedRef: RefObject<boolean>;
  onSwitchContentMode?: () => void;
  hideOnMiniMode?: boolean;
  onCameraChange?: (camera: Camera | undefined) => void;
  canPlayVideo: boolean;
  setAdsPlayer: (el: HTMLVideoElement | null) => void;
  advertisement: Advertisement | undefined;
  isAmbientMode?: boolean;
  /** callback to change the ambient mode, must be memoized to avoid re-rendering */
  onAmbientModeChange?: (isAmbientMode: boolean) => void;
}

const Player = ({
  miniMode,
  preferLiveStreamMutedRef,
  onSwitchContentMode,
  canPlayVideo,
  setAdsPlayer,
  advertisement,
  isAmbientMode,
  onAmbientModeChange,
}: Pick<
  HomeLiveStreamPlayerProps,
  | 'miniMode'
  | 'preferLiveStreamMutedRef'
  | 'onSwitchContentMode'
  | 'canPlayVideo'
  | 'setAdsPlayer'
  | 'advertisement'
  | 'isAmbientMode'
  | 'onAmbientModeChange'
>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { camera } = useHabitatLiveStreamPlayerContext();
  const isMobile = useIsMobileLayout();
  const logged = useSelector((state) => state.user.logged);
  const isFreeUser = useSelector(selectIsFreeUser);

  return !camera ? (
    <HomeVideoPlayer.Player />
  ) : (
    <LiveStreamPlayer.Root streamId={camera.streamKey}>
      <HomeVideoPlayer.Player ref={containerRef}>
        <LiveStreamPlayer.Player />

        {canPlayVideo && (
          <HomeLiveStreamPlayerOverlay
            containerRef={containerRef}
            preferLiveStreamMutedRef={preferLiveStreamMutedRef}
            miniMode={miniMode}
            isAmbientMode={isAmbientMode}
            onAmbientModeChange={onAmbientModeChange}
          />
        )}

        {!miniMode && isFreeUser && (
          <HomeAdvertisementOverlay
            isVisible={!canPlayVideo}
            adsPlayerRef={(el) => setAdsPlayer(el)}
            advertisement={advertisement}
          />
        )}

        {!isMobile && miniMode && onSwitchContentMode && <HomeVideoPlayer.ExpandButton onClick={onSwitchContentMode} />}
      </HomeVideoPlayer.Player>

      {(!logged || isFreeUser) && isMobile && <HomeLiveStreamPlayerFakeMobileControls />}

      {/** NOTE: we can add extra player controls outside of the video here */}

      {/* Virtual components for logging */}
      <LiveStreamPlayer.Logger />
      <LiveStreamPlayer.StatsLogger />
      <LiveStreamPlayer.StatsTrigger />
      <LiveStreamPlayer.VisibilityHandler />
    </LiveStreamPlayer.Root>
  );
};

const CameraChangeNotifier = ({ onCameraChange }: { onCameraChange?: (camera: Camera | undefined) => void }) => {
  const { camera } = useHabitatLiveStreamPlayerContext();
  const onCameraChangeRef = useRef(onCameraChange);
  onCameraChangeRef.current = onCameraChange;

  useEffect(() => {
    onCameraChangeRef.current?.(camera);
  }, [camera]);

  return null;
};

const HomeLiveStreamPlayer = ({
  habitat: selectedHabitat,
  miniMode,
  preferLiveStreamMutedRef,
  onSwitchContentMode,
  hideOnMiniMode,
  onCameraChange,
  canPlayVideo,
  setAdsPlayer,
  advertisement,
  isAmbientMode,
  onAmbientModeChange,
}: HomeLiveStreamPlayerProps) => {
  const isMobile = useIsMobileLayout();
  const { data: fetchedHabitat } = useQuery(getHabitatQueryOptions(selectedHabitat?.slug, selectedHabitat?.zoo.slug));

  // We keep the last fetched habitat to avoid flickering when the habitat is loading
  const lastFetchedHabitatRef = useRef(fetchedHabitat);
  useEffect(() => {
    if (fetchedHabitat) {
      lastFetchedHabitatRef.current = fetchedHabitat;
    }
  }, [fetchedHabitat]);

  const habitat = fetchedHabitat ?? lastFetchedHabitatRef.current;

  return (
    <HomeVideoPlayer.Container miniMode={miniMode} hideOnMiniMode={hideOnMiniMode}>
      {habitat ? (
        <HabitatLiveStreamPlayer.Root habitat={habitat}>
          <Player
            preferLiveStreamMutedRef={preferLiveStreamMutedRef}
            miniMode={miniMode}
            onSwitchContentMode={onSwitchContentMode}
            canPlayVideo={canPlayVideo}
            setAdsPlayer={setAdsPlayer}
            advertisement={advertisement}
            isAmbientMode={isAmbientMode}
            onAmbientModeChange={onAmbientModeChange}
          />
          <CameraChangeNotifier onCameraChange={onCameraChange} />
        </HabitatLiveStreamPlayer.Root>
      ) : (
        // As a placeholder when habitat is loading
        <HomeVideoPlayer.Player />
      )}
      {miniMode && isMobile && (
        <div className="flex min-w-0 grow items-center p-2 pr-4 text-white">
          <div className="flex min-w-0 grow flex-col justify-center px-2">
            <h4 className="preflight preflight-h4 text-sm font-bold">{selectedHabitat.title}</h4>
            <span className="text-xs">{selectedHabitat.zoo.name}</span>
          </div>
          <HomeVideoPlayer.ExpandButton onClick={onSwitchContentMode} />
        </div>
      )}
    </HomeVideoPlayer.Container>
  );
};

export default HomeLiveStreamPlayer;
